import React, { useEffect, useRef, useState } from "react";
import { Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api } from "../../lib/api";
import { confirmSmsCode, sendSmsCode, smsErrorMessage } from "../../lib/firebasePhone";

const RESEND_AFTER_S = 60;

// Mobile number → our backend's OK (/auth/otp/sms/request: account exists, rate limits) → SMS
// code (via Firebase) → onVerified(idToken). Used by sign-in and by forgot-password; the caller
// decides what to do with the verified token. The backend only accepts a Firebase token for a
// number it approved here, and asking first means no SMS is paid for to a number with no account.
export default function SmsOtpForm({ config, onVerified, verifyLabel = "Verify & Sign In", purpose = "login" }) {
  const [step, setStep] = useState("phone"); // phone | code
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const confirmation = useRef(null);
  const captchaBox = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const gate = await api.post("/auth/otp/sms/request", { mobile, purpose });
      confirmation.current = await sendSmsCode(config, `+91${mobile}`, captchaBox.current);
      setStep("code");
      setCode("");
      setCooldown(gate.data?.resend_after || RESEND_AFTER_S);
      toast.success(`Code sent to +91 ${mobile}`);
    } catch (err) { toast.error(smsErrorMessage(err, "Could not send the code")); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const idToken = await confirmSmsCode(confirmation.current, code);
      await onVerified(idToken);
    } catch (err) { toast.error(smsErrorMessage(err, "Verification failed")); } finally { setBusy(false); }
  };

  return (
    <>
      {step === "phone" ? (
        <form onSubmit={send} className="space-y-4" data-testid="sms-otp-phone-form">
          <div className="flex h-12 rounded-md border border-gray-200 bg-gray-50 focus-within:ring-2 focus-within:ring-orange-500 overflow-hidden">
            <span className="flex items-center gap-1.5 pl-3 pr-2 text-sm font-semibold text-gray-600 border-r border-gray-200">
              <Smartphone className="w-4 h-4 text-gray-500" />+91
            </span>
            <input required inputMode="numeric" autoComplete="tel-national" data-testid="sms-otp-mobile"
              value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(-10))}
              placeholder="10-digit mobile number" className="flex-1 bg-transparent px-3 text-sm outline-none" />
          </div>
          <Button type="submit" disabled={busy || mobile.length !== 10} data-testid="sms-otp-send"
            className="w-full h-12 f360-gradient text-white font-semibold text-base border-0 hover:opacity-95">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : "Send code by SMS"}
          </Button>
          <p className="text-[11px] text-gray-500">Use the mobile number saved on your Fire360Pro profile.</p>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-4" data-testid="sms-otp-code-form">
          <p className="text-sm text-gray-600">Enter the 6-digit code sent by SMS to <b>+91 {mobile}</b>.</p>
          <Input required inputMode="numeric" autoComplete="one-time-code" maxLength={6} data-testid="sms-otp-code"
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="••••••" className="bg-gray-50 border-gray-200 h-12 text-center text-2xl tracking-[0.5em] font-mono" />
          <Button type="submit" disabled={busy || code.length !== 6} data-testid="sms-otp-verify"
            className="w-full h-12 f360-gradient text-white font-semibold text-base border-0 hover:opacity-95">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : verifyLabel}
          </Button>
          <div className="flex items-center justify-between text-xs">
            <button type="button" onClick={() => setStep("phone")} className="text-gray-500 hover:text-gray-800">Change number</button>
            <button type="button" disabled={cooldown > 0 || busy} onClick={send} className="text-orange-600 hover:text-orange-700 disabled:text-gray-400">
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>
        </form>
      )}
      {/* Invisible reCAPTCHA Firebase requires on the web */}
      <div ref={captchaBox} />
    </>
  );
}
